/**
 * Lets any signed-in user file a "DA Client Issue Report" ticket from the masthead.
 *
 * Reporters used to open the t.corp template by hand, and agency users outside Amazon could not open
 * it at all. The modal collects the template fields, shows the reporter's name and email as the
 * identity the service will record, and tells internal reporters where their ticket is while agency
 * reporters get a confirmation instead. An attachment goes straight to storage through a presigned URL
 * before the report is filed, because files up to 80 MB do not fit in a request to the service.
 */
import React, { useState } from 'react';
import {
  type Control,
  type FieldErrors,
  type FieldValues,
  type Resolver,
  useController,
  useForm,
  useWatch,
} from 'react-hook-form';
import Alert from '@amzn/meridian/alert';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import FileInput, { FileDetails } from '@amzn/meridian/file-input';
import Input from '@amzn/meridian/input';
import Link from '@amzn/meridian/link';
import Loader from '@amzn/meridian/loader';
import Modal, { ModalFooter } from '@amzn/meridian/modal';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import Textarea from '@amzn/meridian/textarea';
import { type TranslateFn, useTranslation } from 'src/arb-wrapper';
import { FormInput, FormSelect } from 'src/components/common/forms';
import type { IssueReportAttachmentDto, IssueReportResponseDto } from 'src/store/generated/leela-api-generated';
import {
  useIssueReportsControllerCreateAttachmentUploadMutation,
  useIssueReportsControllerCreateMutation,
} from 'src/store/leela-api';
import type { User } from 'src/types';
import { putAttachmentFile } from './attachment-upload';
import { ATTACHMENT_ACCEPT, getAttachmentContentType, validateAttachmentFile } from './file-rules';
import {
  EMPTY_ISSUE_REPORT_FORM_VALUES,
  ISSUE_TYPE_LABEL_KEYS,
  ISSUE_TYPES,
  type IssueReportFormValues,
  mapErrorToMessage,
  translateMessage,
  toRequest,
  type TranslatableMessage,
  validateForm,
} from './issue-report-form-model';

interface IssueReportModalProps {
  readonly reporter: User;
  readonly onClose: () => void;
}

interface MutationError {
  readonly status?: number;
  readonly data?: unknown;
}

// The file input reports files outside `accept` as rejections; keep them so the type error shows inline.
interface FileRejection {
  readonly file: File;
}

const MODAL_WIDTH_PX = 640;
const TEXTAREA_ROWS = 4;
const ATTACHMENT_UPLOAD_FAILED = new Error('Storage did not accept the attachment');

export const IssueReportModal: React.FC<IssueReportModalProps> = ({ reporter, onClose }) => {
  const { t } = useTranslation();
  const [createIssueReport, { isLoading }] = useIssueReportsControllerCreateMutation();
  const [createAttachmentUpload] = useIssueReportsControllerCreateAttachmentUploadMutation();
  const [attachment, setAttachment] = useState<File | null>(null);
  const [isUploadingAttachment, setIsUploadingAttachment] = useState(false);
  const [outcome, setOutcome] = useState<IssueReportResponseDto | null>(null);
  const [submitError, setSubmitError] = useState<TranslatableMessage | null>(null);
  const { control, formState, handleSubmit } = useForm<IssueReportFormValues>({
    defaultValues: EMPTY_ISSUE_REPORT_FORM_VALUES,
    resolver: createResolver(t),
  });
  const sharedFieldControl = control as unknown as Control<FieldValues>;
  const issueType = useWatch({ control, name: 'issueType' });
  const attachmentErrors = attachment ? validateAttachmentFile(attachment) : [];
  const isPending = isLoading || isUploadingAttachment || formState.isSubmitting;

  const uploadAttachment = async (file: File): Promise<IssueReportAttachmentDto> => {
    const contentType = getAttachmentContentType(file);
    setIsUploadingAttachment(true);
    try {
      const { uploadKey, url } = await createAttachmentUpload({
        createAttachmentUploadDto: { filename: file.name, contentType, sizeBytes: file.size },
      }).unwrap();
      if (!await putAttachmentFile({ url, file, contentType })) {
        throw ATTACHMENT_UPLOAD_FAILED;
      }
      return { filename: file.name, contentType, uploadKey };
    } finally {
      setIsUploadingAttachment(false);
    }
  };

  const onSubmit = async (values: IssueReportFormValues) => {
    if (attachmentErrors.length > 0) return;

    setSubmitError(null);
    try {
      const attachmentDto = attachment ? await uploadAttachment(attachment) : undefined;
      const response = await createIssueReport({ createIssueReportDto: toRequest(values, attachmentDto) }).unwrap();
      setOutcome(response);
    } catch (error: unknown) {
      setSubmitError(toSubmitErrorMessage(error));
    }
  };

  return (
    <Modal
      data-testid="issue-report-modal"
      open
      scrollContainer="modal"
      title={t('issue_report_title')}
      width={MODAL_WIDTH_PX}
      onClose={onClose}
    >
      {outcome
        ? <IssueReportOutcome outcome={outcome} onClose={onClose} />
        : (
          <form noValidate onSubmit={handleSubmit(onSubmit)}>
            <Column spacing="400">
              <Input disabled label={t('issue_report_name_label')} value={formatReporterName(reporter)} onChange={noop} />
              <Input disabled label={t('issue_report_email_label')} value={reporter.email ?? ''} onChange={noop} />
              <FormInput control={sharedFieldControl} data-testid="issue-report-team" label={`${t('issue_report_team_label')}*`} name="team" />
              <FormSelect
                control={sharedFieldControl}
                data-testid="issue-report-issue-type"
                label={t('issue_report_issue_type_label')}
                name="issueType"
                options={ISSUE_TYPES.map(value => ({ label: t(ISSUE_TYPE_LABEL_KEYS[value]), value }))}
                required
              />
              {issueType === 'OTHER' && (
                <FormInput
                  control={sharedFieldControl}
                  data-testid="issue-report-other-issue-type"
                  label={`${t('issue_report_other_issue_type_label')}*`}
                  name="otherIssueType"
                />
              )}
              <FormTextarea control={control} label={`${t('issue_report_problem_description_label')}*`} name="problemDescription" />
              <FormTextarea control={control} label={t('issue_report_expected_behavior_label')} name="expectedBehavior" />
              <FormTextarea control={control} label={t('issue_report_actual_behavior_label')} name="actualBehavior" />
              <FormTextarea control={control} label={t('issue_report_business_impact_label')} name="businessImpact" />
              <FormInput
                control={sharedFieldControl}
                data-testid="issue-report-affected-campaign-count"
                label={t('issue_report_affected_campaign_count_label')}
                name="affectedCampaignCount"
              />
              <FormTextarea
                control={control}
                helperText={t('issue_report_example_campaign_links_help')}
                label={t('issue_report_example_campaign_links_label')}
                name="exampleCampaignLinks"
              />
              <Column spacing="200">
                <Text type="b300">{t('issue_report_attachment_label')}</Text>
                <FileInput
                  accept={ATTACHMENT_ACCEPT}
                  data-testid="issue-report-attachment"
                  type="single"
                  uploadButtonLabel={t('issue_report_attachment_upload')}
                  onFileAttached={(files: File[], rejections: FileRejection[]) => {
                    setAttachment(files[0] ?? rejections[0]?.file ?? null);
                  }}
                >
                  {attachment && (
                    <FileDetails
                      error={attachmentErrors.length > 0}
                      errorMessage={attachmentErrors.map(key => t(key)).join(' ')}
                      file={attachment}
                      removeFileButtonLabel={t('issue_report_attachment_remove')}
                      uploadComplete
                      uploadCompleteLabel={t('issue_report_attachment_ready')}
                      onClickRemoveFile={() => { setAttachment(null); }}
                    />
                  )}
                </FileInput>
                {isUploadingAttachment && (
                  <Text data-testid="issue-report-attachment-uploading" type="b200">
                    {t('issue_report_attachment_uploading')}
                  </Text>
                )}
              </Column>
              {submitError && (
                <Alert data-testid="issue-report-submit-error" type="error">
                  {translateMessage(submitError, t)}
                </Alert>
              )}
            </Column>
            <ModalFooter>
              <Row alignmentHorizontal="end" spacing="300">
                <Button data-testid="issue-report-cancel" type="secondary" onClick={onClose}>
                  {t('issue_report_cancel')}
                </Button>
                <Button data-testid="issue-report-submit" disabled={isPending} submit type="primary">
                  {isPending ? <Loader size="small" /> : t('issue_report_submit')}
                </Button>
              </Row>
            </ModalFooter>
          </form>
          )}
    </Modal>
  );
};

const IssueReportOutcome: React.FC<{ outcome: IssueReportResponseDto; onClose: () => void }> = ({ outcome, onClose }) => {
  const { t } = useTranslation();
  return (
    <Column data-testid="issue-report-outcome" spacing="400">
      {outcome.ticketUrl
        ? (
          <Alert type="success">
            {t('issue_report_success_internal', { ticketId: outcome.ticketId ?? '' })}{' '}
            <Link data-testid="issue-report-ticket-link" href={outcome.ticketUrl} rel="noopener noreferrer" target="_blank">
              {t('issue_report_view_ticket')}
            </Link>
          </Alert>
          )
        : <Alert type="success">{t('issue_report_success_external')}</Alert>}
      {outcome.attachmentStatus === 'FAILED' && (
        <Alert data-testid="issue-report-attachment-failed" type="warning">{t('issue_report_attachment_failed')}</Alert>
      )}
      <ModalFooter>
        <Row alignmentHorizontal="end">
          <Button data-testid="issue-report-close" type="primary" onClick={onClose}>{t('issue_report_close')}</Button>
        </Row>
      </ModalFooter>
    </Column>
  );
};

interface FormTextareaProps {
  readonly control: Control<IssueReportFormValues>;
  readonly helperText?: string;
  readonly label: string;
  readonly name: keyof IssueReportFormValues;
}

const FormTextarea: React.FC<FormTextareaProps> = ({ control, helperText, label, name }) => {
  const { field, fieldState } = useController({ control, name });
  return (
    <Textarea
      data-testid={`issue-report-${name}`}
      error={!!fieldState.error}
      errorMessage={fieldState.error?.message}
      helperText={helperText}
      label={label}
      rows={TEXTAREA_ROWS}
      value={field.value}
      onBlur={field.onBlur}
      onChange={field.onChange}
    />
  );
};

function createResolver (t: TranslateFn): Resolver<IssueReportFormValues> {
  return (values) => {
    const errors = Object.fromEntries(
      Object.entries(validateForm(values)).map(([name, message]) => [
        name,
        { type: 'validate', message: translateMessage(message, t) },
      ]),
    ) as FieldErrors<IssueReportFormValues>;
    return Object.keys(errors).length > 0 ? { values: {}, errors } : { values, errors: {} };
  };
}

function toSubmitErrorMessage (error: unknown): TranslatableMessage {
  if (error === ATTACHMENT_UPLOAD_FAILED) {
    return { key: 'issue_report_error_upload_failed' };
  }
  const { status, data } = error as MutationError;
  return mapErrorToMessage(status, data);
}

function formatReporterName (reporter: User): string {
  const fullName = [reporter.givenName, reporter.familyName].filter(Boolean).join(' ');
  return fullName || reporter.username;
}

function noop (): void {}
